import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, HostListener, inject, input, OnDestroy, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ApplicationContextStore, AuthService, TimeClockService, ToastService, TranslationPipe } from '@raptor-fe/services';
import { FeatureFlagTypes } from '@raptor-fe/core';
import { filterVisibleMenuItems } from '../menus/menu-visibility';
import { WebSelectBranchModalStore } from '../modals/web-select-branch-modal/web-select-branch-modal.store';
import { WebLanguageModalComponent } from '../modals/language-modal/web-language-modal.component';
import { DialogService } from '@progress/kendo-angular-dialog';

/** Grace period before the menu collapses so a brief slip off it is forgiven. */
const CLOSE_DELAY_MS = 250;

@Component({
    selector: 'lib-web-sidemenu',
    imports: [RouterModule, TranslationPipe],
    templateUrl: './sidemenu.component.html',
    styleUrl: './sidemenu.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [WebSelectBranchModalStore, TimeClockService],
})
export class SidemenuComponent implements OnDestroy {
    protected readonly authService = inject(AuthService);
    protected readonly applicationContextStore = inject(ApplicationContextStore);
    private readonly webSelectBranchModalStore = inject(WebSelectBranchModalStore);
    private readonly toastService = inject(ToastService);
    private readonly dialogService = inject(DialogService);
    private readonly host = inject(ElementRef<HTMLElement>);

    protected readonly hasTimeClockFeature = signal(false);

    /**
     * Whether the menu offers Logout. A session whose host manages sign-out leaves it to that host.
     * Read once: the menu is only rendered for a logged-in user, and the token is saved before
     * the user is marked logged in, so the component is always created against the session it serves
     * and is rebuilt when a new session starts.
     */
    protected readonly canSignOut = this.resolveCanSignOut();

    /** Whether the last pointer down was a finger, so a tap and a hover can both drive the rail. */
    private pointerIsTouch = false;

    /** Closes the rail on a tap outside it. Attached only while a menu is open, via the effect below. */
    private readonly onOutsideTap = (event: Event): void => {
        if (!this.host.nativeElement.contains(event.target as Node)) this.openKey.set(null);
    };

    constructor() {
        effect(() => {
            const features = this.applicationContextStore.features();
            this.hasTimeClockFeature.set(features.some((f) => f === FeatureFlagTypes.TimeClock));
        });
        effect((onCleanup) => {
            if (this.openKey() === null) return;
            document.addEventListener('click', this.onOutsideTap);
            onCleanup(() => document.removeEventListener('click', this.onOutsideTap));
        });
    }

    menuItems = input.required<MenuItem[]>();

    /**
     * The menu tree filtered to the entries the current user may see. Gating is applied at every
     * level: a top-level entry, a submenu group, and a group leaf are each hidden when the shared
     * predicate rejects them, and children are recursively filtered so a group left with no
     * surviving children is itself removed.
     */
    protected readonly visibleMenuItems = computed(() =>
        filterVisibleMenuItems(this.menuItems(), (key) => this.applicationContextStore.hasPermission(key), this.applicationContextStore.features()),
    );

    /** Key of the hovered entry, or null when the menu is collapsed. */
    protected readonly openKey = signal<string | null>(null);

    /** Whether the panel is open. True whenever an entry is active. */
    protected readonly expanded = computed(() => this.openKey() !== null);

    private closeTimer?: ReturnType<typeof setTimeout>;

    /** Opens the panel and shows this entry's submenu, cancelling any pending close. */
    protected open(key: string): void {
        clearTimeout(this.closeTimer);
        this.openKey.set(key);
    }

    /** Remembers whether the current interaction is touch, used to route the tap handler below. */
    @HostListener('pointerdown', ['$event'])
    protected trackPointer(event: PointerEvent): void {
        this.pointerIsTouch = event.pointerType === 'touch';
    }

    /** Hover open for a mouse or pen. A finger is ignored here and opens on tap instead. */
    protected onEntryEnter(key: string, event: PointerEvent): void {
        if (event.pointerType === 'touch') return;
        if (key === 'profile') this.applicationContextStore.fetchWorkDayClockStatus();
        this.open(key);
    }

    /** Tap toggle for a finger, and stops the click from closing the panel via closeNow. */
    protected onEntryTap(key: string, event: Event): void {
        if (!this.pointerIsTouch) return;
        event.stopPropagation();
        if (key === 'profile') this.applicationContextStore.fetchWorkDayClockStatus();
        if (this.openKey() === key) {
            this.openKey.set(null);
        } else {
            this.open(key);
        }
    }

    /** Collapses after a short delay so a tiny slip off the menu does not close it. Mouse only. */
    protected scheduleClose(): void {
        if (this.pointerIsTouch) return;
        clearTimeout(this.closeTimer);
        this.closeTimer = setTimeout(() => this.openKey.set(null), CLOSE_DELAY_MS);
    }

    /** Collapses right away on click so the menu does not linger after navigating. */
    @HostListener('click')
    protected closeNow(): void {
        clearTimeout(this.closeTimer);
        this.openKey.set(null);
    }

    ngOnDestroy(): void {
        clearTimeout(this.closeTimer);
    }

    logout(): void {
        this.authService?.logout();
    }

    private resolveCanSignOut(): boolean {
        const isSignOutManagedByHost = this.authService.isSignOutManagedByHost();

        if (isSignOutManagedByHost) {
            return false;
        }

        return true;
    }

    protected openBranchSelectModal(): void {
        this.webSelectBranchModalStore.open();
    }

    protected openLanguageModal(): void {
        WebLanguageModalComponent.open(this.dialogService);
    }

    protected async toggleClockStatus(): Promise<void> {
        const result = await this.toastService.showYesOrNoQuestion(
            `Clock ${this.applicationContextStore.isWorkDayClockedIn() ? 'Out' : 'In'}?`,
            `Are you sure you want to ${this.applicationContextStore.isWorkDayClockedIn() ? 'clock out' : 'clock in'} for the work day?`,
        );
        if (result) this.applicationContextStore.toggleDayClockedIn();
    }
}

export interface MenuItem {
    text: string;
    iconClass?: string;
    route?: string;
    children?: MenuItem[];
    features?: string[];
    /**
     * Permission `:view` key required to show this item (feature-visibility only). Accepts a single
     * key or an any-of array of keys; when an array is given the item shows if the user holds at
     * least one, mirroring the any-of semantics of `hasPermissionGuard` and `libHasPermission` so a
     * menu entry can match a route guarded on multiple keys.
     */
    viewPermission?: string | string[];
}
